import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import {
  useFetchShelvesTemplate,
  useShelveBulkDocMutation,
} from "../../../hooks/shelveHooks";
import {
  styleAndProtectDummyRow,
  cleanUploadedExcelFile,
} from "../../../utilities/bulkUploadHelper";
import { useLanguage } from "../../../context/LanguageContext";

type ShelveBulkUploadModalsProps = {
  onClose: () => void;
  onResponseOpene: () => void;
  warehouseId?: string;
};

export default function ShelveBulkUploadModals({
  onClose,
  onResponseOpene,
  warehouseId,
}: ShelveBulkUploadModalsProps) {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } =
    useShelveBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } =
    useFetchShelvesTemplate();

  const handleUploadDoc = async () => {
    if (!file) {
      toast.error(t("Please select a file first"));
      return;
    }
    if (!warehouseId) {
      toast.error(t("Warehouse ID is missing"));
      return;
    }

    const cleanedFile = await cleanUploadedExcelFile(file, templateData?.[0]);

    const formData = new FormData();
    formData.append("shelves", cleanedFile);
    formData.append("warehouseId", warehouseId);

    uploadDocMutate(formData, {
      onSuccess: () => {
        onClose();
        onResponseOpene();
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(t(error.message || "Failed to upload shelves data"));
      },
    });
  };

  const handleDownloadTemplate = async () => {
    if (!templateData || templateData.length === 0) {
      toast.error(t("Template data is not loaded yet"));
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Shelves Template");

    // Row 1: Info
    const infoRow = worksheet.addRow([
      "This template is for importing warehouse shelves data. Fields are mandatory. Please do not modify the first 4 rows (Row 4 is sample/dummy data).",
    ]);
    worksheet.mergeCells(`A1:G1`);
    infoRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFD9" },
      };
      cell.font = {
        name: "Arial",
        size: 10,
        bold: true,
        color: { argb: "212121" },
      };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });

    infoRow.height = 25;

    // Row 2: Validation row
    const validationRow = worksheet.addRow([
      "Area Name is required (e.g., Area A)",
      "Row Name is required (e.g., Row 1)",
      "Rack Name is required (e.g., Rack 1)",
      "Level Name is required (e.g., Level 1)",
      "Location Type is required (Bin or Pallet)",
      "Location Name is required (e.g., Bin 1)",
      "Storage Type is required (B2C or B2B)",
    ]);
    validationRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFCCCC" },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      cell.font = { color: { argb: "212121" }, size: 9 };

      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    validationRow.height = 45;

    // Row 3: Headings
    const headings = Object.keys(templateData[0] || {});
    const headingsRow = worksheet.addRow(headings);

    headingsRow.eachCell((cell: any) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "217447" },
      };
      cell.font = { name: "Arial", bold: true, color: { argb: "ffffff" } };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FF000000" } },
        left: { style: "thin", color: { argb: "FF000000" } },
        bottom: { style: "thin", color: { argb: "FF000000" } },
        right: { style: "thin", color: { argb: "FF000000" } },
      };
    });
    headingsRow.height = 25;

    // Row 4: Dummy Data Row
    let dummyRow: ExcelJS.Row | undefined;
    if (templateData && templateData.length > 0 && templateData[0]) {
      const dummyValues = Object.values(templateData[0]);
      dummyRow = worksheet.addRow(dummyValues);
      dummyRow.eachCell((cell: any) => {
        cell.font = { name: "Arial", size: 10, color: { argb: "212121" } };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
      dummyRow.height = 20;
    }

    // Adjust column widths
    worksheet.columns = [
      { width: 25 }, // Area
      { width: 25 }, // Row
      { width: 25 }, // Rack
      { width: 25 }, // Level
      { width: 25 }, // Location Type
      { width: 25 }, // Location Name
      { width: 25 }, // Storage Type
    ];

    await styleAndProtectDummyRow(worksheet, dummyRow);

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "shelves-bulk-template.xlsx");
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="rounded-2xl ">
        {/* Header */}
        <div className="flex items-center">
          <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px] ">
            <h2 className="text-2xl">{t("Upload Shelve Data")}</h2>
          </div>
          <div className="relative ">
            <button
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
          <div className="w-[655px] space-y-4">
            <FileUploader
              name="photo"
              extraParentClasses="col-span-3"
              img="/img/upload-img.png"
              title={t("Upload Photo")}
              message={t("Upload an Excel or CSV file only (Max size: 2 MB).")}
              classNameMain="bg-transparent border-none flex items-center gap-3"
              fileVal={file}
              setFileVal={(file: any) => {
                setFile(file);
              }}
              showPreview={true}
              required
            />

            {file && (
              <p className="text-sm text-green-600 font-medium">
                {t("Selected File: ")}{file.name}
              </p>
            )}

            <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
              <li>{t("Download the Excel/CSV template before uploading.")}</li>
              <li>{t("Do not change column headers.")}</li>
              <li>
                {t("Mandatory fields: Area Name, Row Name, Rack Name, Level Name, Location Type, Location Name, and Storage Type.")}
              </li>
            </ul>

            <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
              <p className="text-xs text-[#212121] font-medium">
                <b>{t("Note")}</b> {t(": Please upload the file in the same format as the downloaded file.")}
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm"
              >
                <img src="/img/csvxl.svg" alt="" />
                <span>{t("Download")}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px]">
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading}
            className="bg-[#FBD037] hover:bg-yellow-400 text-[#000000] flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isBulkUploading ? t("Uploading...") : t("Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
